import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import vpgLogoCondensed from '../../assets/images/flexcoa-logo-condensed.png';
import vpgLogoHome from '../../assets/images/flexcoa-logo-condensed-green.png';
import { Menu as MenuIcon, X, ChevronDown, ChevronsUpDown, Bell, PanelLeftClose, PanelLeftOpen, LogOut, LayoutDashboard, UserCircle, Wrench, HelpCircle, BookOpen, Building2, FileText, Home, Rocket } from 'lucide-react';
import NotificationBell from '../Notifications/NotificationBell';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import studyUtil from '../../utils/StudyUtil';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout';
import { isParticipantDomain } from '../../utils/participantDomain';
import { usesStaffShell } from '../../utils/appShell';
import { requestSurveyExit } from '../../utils/surveyExitEvent';
import { useTheme } from '../../contexts/ThemeContext';
import './hideParticipantButton.css'; // Import the CSS file to hide the button

// Items that belong to the account menu; everything else is a main section
// shown in the header bar (FECOA-2174).
const ACCOUNT_ITEMS = new Set(['Select Sponsor', 'Profile', 'FAQ', 'Development', 'Logout']);

const readSelectedSponsor = () => {
  try {
    return JSON.parse(localStorage.getItem('selectedSponsor') || 'null');
  } catch {
    return null;
  }
};

const initialsFor = (user, userType) => {
  if (userType === 'participant') return 'P';
  const first = user?.first_name?.trim?.()?.[0];
  const last = user?.last_name?.trim?.()?.[0];
  if (first || last) return `${first || ''}${last || ''}`.toUpperCase();
  const source = user?.username || user?.email || '';
  return (source.trim()[0] || 'U').toUpperCase();
};

const HomeNavMenu = ({ isAuthenticated, setIsAuthenticated, isMenuOpen, setIsMenuOpen, isDev, sidebarCollapsed = false, onToggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, userType, userPermissions } = useContext(AuthContext);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const paths = location.pathname.split('/').filter(x => x);
  const [hasOnlyOneStudy, setHasOnlyOneStudy] = useState(null); // null = checking, false = multiple studies, true = one study
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLocalDevMode, setIsLocalDevMode] = useState(() => isLocalDevLayoutActive());
  const [selectedSponsor, setSelectedSponsor] = useState(readSelectedSponsor);
  // NotificationBell (in the page's top bar) shares its unread count for the sidebar.
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const syncSponsor = () => setSelectedSponsor(readSelectedSponsor());
    const onUnread = (event) => setUnreadCount(Number(event.detail) || 0);
    window.addEventListener('storage', syncSponsor);
    window.addEventListener('flexcoa:unread-count', onUnread);
    return () => {
      window.removeEventListener('storage', syncSponsor);
      window.removeEventListener('flexcoa:unread-count', onUnread);
    };
  }, []);

  useEffect(() => {
    setSelectedSponsor(readSelectedSponsor());
  }, [location.pathname]);

  // Match LocalDevBanner + screen builder without waiting for body.local-dev-mode (see localDevLayout.js)
  useEffect(() => {
    const sync = () => setIsLocalDevMode(isLocalDevLayoutActive());

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const noMenuPaths = ['', 'login', 'forgot-password', 'register', 'assessment'];
  const isSponsorSelector = paths[0] === 'sponsor-selector';
  const isParticipantDashboard = paths[0] === 'participant-dashboard';
  const isSurveyTaking = paths[0] === 'survey-taking';
  const isFAQ = paths[0] === 'faq';
  const isParticipantFlow = userType === 'participant' || isParticipantDashboard || isSurveyTaking || isFAQ;

  // Participant routes must always render in light mode — strip the `dark` class
  // from <html> while a participant flow is active and prevent ThemeProvider from
  // re-adding it via a MutationObserver.
  useEffect(() => {
    if (!isParticipantFlow) return;

    const ensureLight = () => document.documentElement.classList.remove('dark');
    ensureLight();

    const observer = new MutationObserver(ensureLight);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (resolvedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    };
  }, [isParticipantFlow, resolvedTheme]);

  const shouldShowMenu = !noMenuPaths.includes(paths[0]);

  // Check if user has only one study
  useEffect(() => {
    const checkStudyCount = async () => {
      if (user && !isParticipantFlow) {
        // Skip study count check if user is internal and has no valid sponsor selected
        if (user.is_internal) {
          try {
            const selectedSponsorStr = localStorage.getItem('selectedSponsor');
            if (!selectedSponsorStr) {
              console.debug('[HOME_NAV_MENU] Skipping study count check - no sponsor selected');
              setHasOnlyOneStudy(null);
              return;
            }
            const selectedSponsor = JSON.parse(selectedSponsorStr);
            if (!selectedSponsor || !selectedSponsor.id) {
              console.debug('[HOME_NAV_MENU] Skipping study count check - invalid sponsor data');
              setHasOnlyOneStudy(null);
              return;
            }
          } catch (e) {
            console.debug('[HOME_NAV_MENU] Skipping study count check - error parsing sponsor:', e);
            setHasOnlyOneStudy(null);
            return;
          }
        }

        try {
          console.debug('[HOME_NAV_MENU] Starting study count check for user:', user.email);
          // Clear cache to force fresh check
          studyUtil.clearCache();
          const onlyOneStudy = await studyUtil.hasOnlyOneStudy();
          console.debug('[HOME_NAV_MENU] Study count check result:', onlyOneStudy);
          setHasOnlyOneStudy(onlyOneStudy);
        } catch (error) {
          console.error('[HOME_NAV_MENU] Error checking study count:', error);
        }
      } else {
        console.debug('[HOME_NAV_MENU] No user or participant flow, skipping study count check');
      }
    };

    checkStudyCount();
  }, [user, isParticipantFlow]);

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set data-page attribute on body when on participant pages
  useEffect(() => {
    const isParticipantPage = location.pathname.includes('participant');

    if (isParticipantPage) {
      document.body.setAttribute('data-page', 'participant');
    } else {
      document.body.removeAttribute('data-page');
    }

    return () => {
      document.body.removeAttribute('data-page');
    };
  }, [location.pathname]);

  // A new page closes the mobile menu.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Check if current path matches menu item
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname.startsWith(`/${path}`);
  };

  const handleLogoClick = () => {
    if (isSurveyTaking) {
      // FECOA-1451: never leave an active assessment silently — SurveyTaking
      // listens for this request and owns the save-and-exit confirmation.
      requestSurveyExit();
      return;
    }
    if (isParticipantFlow && userType === 'participant') {
      navigate('/participant-dashboard');
    } else {
      navigate('/studies');
    }
  };

  let menuItems = isParticipantFlow ? [
    {
      icon: HelpCircle,
      label: 'FAQ',
      path: 'faq',
      onClick: () => navigate('/faq')
    },
    {
      icon: LogOut,
      label: 'Logout',
      path: 'logout',
      onClick: async () => {
        try {
          // Let the AuthContext handle redirection based on user type
          await logout();
          // No navigation here - AuthContext will redirect to /#studycode
        } catch (error) {
          console.error('Logout failed:', error);
          // Force logout even if API call fails
          setIsAuthenticated(false);
          // No navigation - let AuthContext handle it
        }
      }
    }
  ] : [
    // Only show Studies option if user has more than one study. With a single
    // study, /studies opens that study, so the link is called Study (FECOA-2174).
    ...(hasOnlyOneStudy === false ? [{
      icon: LayoutDashboard,
      label: 'Studies',
      path: 'studies',
      onClick: () => navigate('/studies')
    }] : []),
    ...(hasOnlyOneStudy === true ? [{
      icon: LayoutDashboard,
      label: 'Study',
      path: 'studies',
      onClick: () => navigate('/studies')
    }] : []),
    // Add sponsor selector.
    // For internal users, keep the original behavior of only showing when there are multiple sponsors.
    // For superusers, always show this option regardless of sponsor count so they can jump into the selector.
    ...(((user?.is_internal && userPermissions?.can_select_sponsor && userPermissions?.sponsor_count > 1) || user?.is_superuser) ? [{
      icon: Building2,
      label: 'Select Sponsor',
      path: 'sponsor-selector',
      onClick: () => navigate('/sponsor-selector', { state: { from: 'mainMenu' } })
    }] : []),
    // Add Study Builder for users with can_access_study_builder permission
    ...(user?.is_internal && userPermissions?.can_access_study_builder ? [{
      icon: BookOpen,
      label: 'Study Builder',
      path: 'studybuilder',
      onClick: () => navigate('/studybuilder')
    }] : []),
    // Add Assessment Builder for users with can_access_assessment_builder permission
    ...(user?.is_internal && userPermissions?.can_access_assessment_builder ? [{
      icon: FileText,
      label: 'Assessment Builder',
      path: 'assessment-builder',
      onClick: () => navigate('/assessment-builder')
    }] : []),
    // Add Study Promotion for users with the promotion permission (FECOA-1919)
    ...(user?.is_internal && userPermissions?.can_promote_study_build ? [{
      icon: Rocket,
      label: 'Study Promotion',
      path: 'study-promotion',
      onClick: () => navigate('/study-promotion')
    }] : []),
    {
      icon: UserCircle,
      label: 'Profile',
      path: 'profile',
      onClick: () => navigate('/profile')
    },
    {
      icon: LogOut,
      label: 'Logout',
      path: 'logout',
      onClick: async () => {
        try {
          // Let the AuthContext handle redirection based on user type
          await logout();
          // No manual navigation - AuthContext will handle it
        } catch (error) {
          console.error('Logout failed:', error);
          // Force logout even if API call fails
          setIsAuthenticated(false);
          // No manual navigation - let AuthContext handle it
        }
      }
    }
  ];

  if (isDev && !isParticipantFlow) {
    menuItems.splice(menuItems.length - 1, 0, {
      icon: Wrench,
      label: 'Development',
      path: 'development',
      onClick: () => navigate('/development')
    });
  }

  // On the sponsor selector screen, only show Profile and Logout
  if (isSponsorSelector) {
    menuItems = menuItems.filter(item => item.label === 'Profile' || item.label === 'Logout');
  }

  const sectionItems = menuItems.filter(item => !ACCOUNT_ITEMS.has(item.label));
  const accountItems = menuItems.filter(item => ACCOUNT_ITEMS.has(item.label));

  // Close menus when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Check for participant login page with hash pattern
  const isParticipantLoginWithHash = window.location.hash && window.location.hash.length > 1;

  // Set participant page flags when on login hash pages
  useEffect(() => {
    const checkHash = () => {
      // Specifically check for hash-based participant login
      const isParticipantLoginPage = window.location.hash && window.location.hash.length > 1;
      if (isParticipantLoginPage) {
        document.body.setAttribute('data-page', 'participant-login');
      } else if (location.pathname.includes('participant')) {
        document.body.setAttribute('data-page', 'participant');
      } else {
        document.body.removeAttribute('data-page');
      }
    };

    // Initial check
    checkHash();

    // Set up hash change listener
    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
      document.body.removeAttribute('data-page');
    };
  }, [location.pathname]);

  /* Match Layout participantPortalForceLight: global `dark:` still wins inside .force-light-mode unless we omit dark variants here. */
  const isParticipantPortalLoginShell =
    location.pathname === '/login/participant' ||
    location.pathname.startsWith('/home/') ||
    (location.pathname === '/' && isParticipantDomain());

  // Dark-mode classes are left out where the page is always light.
  const dk = (classes) => (isParticipantPortalLoginShell ? '' : classes);

  const headerSurface = `border-b border-gray-200 bg-white ${dk('dark:border-gray-700 dark:bg-gray-900')}`;

  const iconButton = `relative inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${dk('dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white')}`;

  const sectionLink = (active) =>
    `inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
      active
        ? `bg-gray-100 text-gray-900 ${dk('dark:bg-gray-800 dark:text-white')}`
        : `text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${dk('dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white')}`
    }`;

  const menuRow = (item) => {
    const active = isActive(item.path);
    const isLogout = item.label === 'Logout';
    return `group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
      isLogout
        ? `text-danger-600 hover:bg-danger-50 ${dk('dark:text-danger-400 dark:hover:bg-danger-500/10')}`
        : active
          ? `bg-gray-100 font-medium text-gray-900 ${dk('dark:bg-gray-700/60 dark:text-white')}`
          : `text-gray-700 hover:bg-gray-100 hover:text-gray-900 ${dk('dark:text-gray-300 dark:hover:bg-gray-700/60 dark:hover:text-white')}`
    }`;
  };

  const menuIcon = (item) =>
    item.label === 'Logout'
      ? 'h-4 w-4 flex-shrink-0'
      : `h-4 w-4 flex-shrink-0 ${isActive(item.path) ? `text-primary-600 ${dk('dark:text-primary-400')}` : `text-gray-400 group-hover:text-gray-600 ${dk('dark:text-gray-500 dark:group-hover:text-gray-300')}`}`;

  const runItem = (item, close) => {
    item.onClick();
    close(false);
  };

  // Participant pages are always drawn light (see the effect above), so they keep the green logo.
  const showWhiteLogo = isDarkMode && !isParticipantPortalLoginShell && !isParticipantFlow;
  const displayName = userType === 'participant' ? 'Participant' : (user?.username || user?.email || 'User');
  const showAccountArea = isAuthenticated && shouldShowMenu;

  // Staff pages: a left sidebar instead of the top header (FECOA-2174).
  const isStaffShell = shouldShowMenu && usesStaffShell({ isAuthenticated, userType, pathname: location.pathname });

  if (isStaffShell) {
    const canSwitchSponsor =
      (user?.is_internal && userPermissions?.can_select_sponsor && userPermissions?.sponsor_count > 1) || user?.is_superuser;
    const sponsorName = selectedSponsor?.name || user?.sponsor_name || '';
    const sponsorInitials = sponsorName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.email || 'User';
    const toolItems = accountItems.filter((item) => item.label === 'Development');
    const popoverItems = accountItems.filter((item) => item.label !== 'Development');
    const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount);

    const sidebarLink = (active) =>
      `group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
        active
          ? 'bg-white font-semibold text-gray-900 shadow-xs ring-1 ring-gray-200/70 dark:bg-white/[0.07] dark:text-white dark:ring-white/10'
          : 'font-medium text-gray-600 hover:bg-gray-900/[0.04] hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/[0.04] dark:hover:text-white'
      }`;
    const sidebarIcon = (active) =>
      `h-4 w-4 flex-shrink-0 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'}`;

    const logoButton = (
      <button
        type="button"
        className="flex flex-shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60"
        onClick={handleLogoClick}
        aria-label="flexCOA home"
      >
        <img className="h-8 w-auto" src={showWhiteLogo ? vpgLogoCondensed : vpgLogoHome} alt="FlexCOA Logo" />
      </button>
    );

    const SponsorTag = canSwitchSponsor ? 'button' : 'div';
    const sponsorBlock = sponsorName ? (
      <SponsorTag
        {...(canSwitchSponsor
          ? {
              type: 'button',
              onClick: () => navigate('/sponsor-selector', { state: { from: 'mainMenu' } }),
              'aria-label': `Change sponsor (current: ${sponsorName})`,
            }
          : {})}
        className={`flex w-full items-center gap-2.5 rounded-lg border border-gray-200/80 bg-white px-2.5 py-2 text-left shadow-xs dark:border-white/10 dark:bg-white/[0.04] ${
          canSwitchSponsor ? 'transition-colors hover:border-gray-300 dark:hover:border-white/20' : ''
        }`}
      >
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-900 text-[11px] font-semibold text-white dark:bg-white dark:text-gray-950">
          {sponsorInitials || <Building2 className="h-3.5 w-3.5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight text-gray-900 dark:text-gray-50">{sponsorName}</span>
          <span className="block text-[11.5px] text-gray-500 dark:text-gray-400">Sponsor</span>
        </span>
        {canSwitchSponsor && <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-gray-400" />}
      </SponsorTag>
    ) : null;

    const notificationsLink = (
      <button
        type="button"
        onClick={() => navigate('/notifications')}
        className={sidebarLink(isActive('notifications'))}
        aria-current={isActive('notifications') ? 'page' : undefined}
      >
        <Bell className={sidebarIcon(isActive('notifications'))} strokeWidth={1.75} />
        <span className="flex-1">Notifications</span>
        {unreadCount > 0 && (
          <span className="rounded-full bg-primary-600 px-1.5 py-px text-[11px] font-semibold tabular-nums text-white">
            {unreadLabel}
          </span>
        )}
      </button>
    );

    const avatar = (size = 'h-8 w-8 text-[12px]') => (
      <span className={`flex ${size} flex-shrink-0 items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-800 dark:text-primary-300`}>
        {initialsFor(user, userType)}
      </span>
    );

    return (
      <>
        {/* Sidebar (large screens). Collapses to an icon rail; Layout remembers the choice. */}
        <aside
          className={`fixed bottom-0 left-0 z-40 hidden flex-col pb-3 pt-5 transition-[width] duration-200 ease-out lg:flex ${
            sidebarCollapsed ? 'w-[68px] items-center overflow-visible px-2.5' : 'w-[248px] overflow-y-auto px-3'
          } ${isLocalDevMode ? 'top-8' : 'top-0'}`}
          aria-label="Sidebar"
        >
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                className="flex h-8 w-8 items-center overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60"
                onClick={handleLogoClick}
                aria-label="flexCOA home"
                title="flexCOA"
              >
                <img className="h-8 w-auto max-w-none object-left" src={showWhiteLogo ? vpgLogoCondensed : vpgLogoHome} alt="FlexCOA Logo" />
              </button>
              {onToggleSidebar && (
                <button
                  type="button"
                  onClick={onToggleSidebar}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-900/[0.05] hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                >
                  <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between pl-2">
              {logoButton}
              {onToggleSidebar && (
                <button
                  type="button"
                  onClick={onToggleSidebar}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-900/[0.05] hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar"
                >
                  <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
                </button>
              )}
            </div>
          )}

          {sponsorBlock && (
            <div className={sidebarCollapsed ? 'mt-5' : 'mt-6 w-full'}>
              {sidebarCollapsed ? (
                <SponsorTag
                  {...(canSwitchSponsor
                    ? {
                        type: 'button',
                        onClick: () => navigate('/sponsor-selector', { state: { from: 'mainMenu' } }),
                        'aria-label': `Change sponsor (current: ${sponsorName})`,
                      }
                    : {})}
                  title={sponsorName}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-gray-950"
                >
                  {sponsorInitials || <Building2 className="h-3.5 w-3.5" />}
                </SponsorTag>
              ) : (
                sponsorBlock
              )}
            </div>
          )}

          <nav className={`mt-6 space-y-0.5 ${sidebarCollapsed ? '' : 'w-full'}`} aria-label="Main">
            {sectionItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.onClick}
                className={`${sidebarLink(isActive(item.path))} ${sidebarCollapsed ? 'h-9 !w-9 justify-center !px-0' : ''}`}
                aria-current={isActive(item.path) ? 'page' : undefined}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <item.icon className={sidebarIcon(isActive(item.path))} strokeWidth={1.75} />
                <span className={sidebarCollapsed ? 'sr-only' : ''}>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className={`mt-6 space-y-0.5 border-t border-gray-900/[0.06] pt-4 dark:border-white/[0.06] ${sidebarCollapsed ? '' : 'w-full'}`}>
            {sidebarCollapsed ? (
              <button
                type="button"
                onClick={() => navigate('/notifications')}
                className={`${sidebarLink(isActive('notifications'))} relative h-9 !w-9 justify-center !px-0`}
                aria-current={isActive('notifications') ? 'page' : undefined}
                title="Notifications"
              >
                <Bell className={sidebarIcon(isActive('notifications'))} strokeWidth={1.75} />
                <span className="sr-only">Notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-canvas" aria-hidden="true" />
                )}
              </button>
            ) : (
              notificationsLink
            )}
            {toolItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.onClick}
                className={`${sidebarLink(isActive(item.path))} ${sidebarCollapsed ? 'h-9 !w-9 justify-center !px-0' : ''}`}
                aria-current={isActive(item.path) ? 'page' : undefined}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <item.icon className={sidebarIcon(isActive(item.path))} strokeWidth={1.75} />
                <span className={sidebarCollapsed ? 'sr-only' : ''}>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Account */}
          <div className={`relative mt-auto pt-4 ${sidebarCollapsed ? '' : 'w-full'}`} ref={menuRef}>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  className={`absolute bottom-full z-50 mb-2 rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800 ${
                    sidebarCollapsed ? 'left-0 w-56' : 'left-0 right-0'
                  }`}
                >
                  {popoverItems.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => runItem(item, setIsMenuOpen)}
                      className={menuRow(item)}
                    >
                      <item.icon className={menuIcon(item)} strokeWidth={1.75} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`flex items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-gray-900/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.04] ${
                sidebarCollapsed ? 'justify-center p-1' : 'w-full px-2 py-2'
              }`}
              aria-label="Menu"
              aria-expanded={isMenuOpen}
              title={sidebarCollapsed ? fullName : undefined}
            >
              {avatar()}
              {!sidebarCollapsed && (
                <>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-gray-900 dark:text-gray-50">{fullName}</span>
                    {user?.email && <span className="block truncate text-[11.5px] text-gray-500 dark:text-gray-400">{user.email}</span>}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </>
              )}
            </button>
          </div>
        </aside>

        {/* Small screens: a top bar and a slide-in menu */}
        <div ref={mobileMenuRef} className={`sticky z-40 lg:hidden ${isLocalDevMode ? 'top-8' : 'top-0'}`}>
          <div className="flex h-14 items-center justify-between border-b border-gray-200/80 bg-white px-4 dark:border-white/10 dark:bg-gray-900">
            {logoButton}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className={iconButton}
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
            >
              <MenuIcon className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-gray-950/40" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
              <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-canvas px-3 pb-4 pt-4 shadow-overlay">
                <div className="flex items-center justify-between pl-2">
                  {logoButton}
                  <button type="button" onClick={() => setIsMobileMenuOpen(false)} className={iconButton} aria-label="Close menu">
                    <X className="h-5 w-5" strokeWidth={1.75} />
                  </button>
                </div>
                {sponsorBlock && <div className="mt-5">{sponsorBlock}</div>}
                <nav className="mt-5 space-y-0.5" aria-label="Mobile">
                  {sectionItems.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => runItem(item, setIsMobileMenuOpen)}
                      className={sidebarLink(isActive(item.path))}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                    >
                      <item.icon className={sidebarIcon(isActive(item.path))} strokeWidth={1.75} />
                      {item.label}
                    </button>
                  ))}
                </nav>
                <div className="mt-4 space-y-0.5 border-t border-gray-900/[0.06] pt-4 dark:border-white/[0.06]">
                  {notificationsLink}
                  {accountItems.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => runItem(item, setIsMobileMenuOpen)}
                      className={menuRow(item)}
                    >
                      <item.icon className={menuIcon(item)} strokeWidth={1.75} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-auto flex items-center gap-2.5 px-2 pt-6">
                  {avatar()}
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold text-gray-900 dark:text-gray-50">{fullName}</span>
                    {user?.email && <span className="block truncate text-[11.5px] text-gray-500 dark:text-gray-400">{user.email}</span>}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <header className={`fixed ${isLocalDevMode ? 'top-8' : 'top-0'} left-0 right-0 z-50 transition-[background-color,box-shadow] duration-200 ${headerSurface}`}>
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <button
          type="button"
          className="flex flex-shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60"
          onClick={handleLogoClick}
          aria-label="flexCOA home"
        >
          <img
            className="h-8 w-auto sm:h-9"
            src={showWhiteLogo ? vpgLogoCondensed : vpgLogoHome}
            alt="FlexCOA Logo"
          />
        </button>

        {/* FECOA-1451 — clear way back to the dashboard while taking an
             assessment. Routed through the exit-request event so SurveyTaking
             can confirm and save before leaving. */}
        {isSurveyTaking && (
          <button
            type="button"
            onClick={requestSurveyExit}
            className={`ml-1 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${dk('dark:text-gray-300 dark:hover:bg-gray-800')}`}
            aria-label="Return to dashboard"
          >
            <Home size={16} strokeWidth={1.75} />
            <span>Dashboard</span>
          </button>
        )}

        {/* Main sections */}
        {showAccountArea && sectionItems.length > 0 && (
          <>
            <span className={`mx-2 hidden h-6 w-px bg-gray-200 md:block ${dk('dark:bg-gray-700')}`} aria-hidden="true" />
            <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
              {sectionItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  className={sectionLink(isActive(item.path))}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <item.icon className="h-4 w-4" strokeWidth={1.75} />
                  {item.label}
                </button>
              ))}
            </nav>
          </>
        )}

        {/* Right side */}
        <div className="ml-auto flex items-center gap-1.5">
          {!isAuthenticated && !paths.includes('login') && !isParticipantLoginWithHash ? (
            <div className="sign-in-button-container">
              <Link
                to="/login"
                className="sign-in-button btn btn-primary"
              >
                Sign in
              </Link>
            </div>
          ) : showAccountArea ? (
            <>
              {/* Notification Bell - shown for authenticated non-participant users */}
              {!isParticipantFlow && (
                <NotificationBell isScrolled />
              )}

              {/* Account menu (desktop) */}
              <div className="relative hidden md:block" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${dk('dark:hover:bg-gray-800')}`}
                  aria-label="Menu"
                  aria-expanded={isMenuOpen}
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-[13px] font-semibold text-white ring-2 ring-white ${dk('dark:ring-gray-900')}`}>
                    {initialsFor(user, userType)}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.12, ease: 'easeOut' }}
                      className={`absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-lg border border-gray-200 bg-white p-1.5 shadow-float ${dk('dark:border-gray-700 dark:bg-gray-800')}`}
                    >
                      {user && (
                        <div className={`mb-1 border-b border-gray-100 px-3 pb-3 pt-2 ${dk('dark:border-gray-700')}`}>
                          <p className={`truncate text-sm font-medium text-gray-900 ${dk('dark:text-gray-50')}`}>{displayName}</p>
                          {userType !== 'participant' && (
                            <p className={`truncate text-xs text-gray-500 ${dk('dark:text-gray-400')}`}>
                              {user?.is_internal ? 'Internal User' : 'User'}
                            </p>
                          )}
                        </div>
                      )}
                      {accountItems.map((item) => (
                        <button
                          key={item.label}
                          type="button"
                          onClick={() => runItem(item, setIsMenuOpen)}
                          className={menuRow(item)}
                        >
                          <item.icon className={menuIcon(item)} strokeWidth={1.75} />
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Everything in one menu on small screens */}
              <div className="md:hidden" ref={mobileMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen((open) => !open)}
                  className={iconButton}
                  aria-label="Open menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? <X className="h-5 w-5" strokeWidth={1.75} /> : <MenuIcon className="h-5 w-5" strokeWidth={1.75} />}
                </button>
                <AnimatePresence>
                  {isMobileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className={`absolute inset-x-0 top-16 border-b border-gray-200 bg-white px-4 pb-4 pt-2 shadow-float ${dk('dark:border-gray-700 dark:bg-gray-900')}`}
                    >
                      {user && (
                        <div className="flex items-center gap-3 px-3 py-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white">
                            {initialsFor(user, userType)}
                          </span>
                          <div className="min-w-0">
                            <p className={`truncate text-sm font-medium text-gray-900 ${dk('dark:text-gray-50')}`}>{displayName}</p>
                            {userType !== 'participant' && (
                              <p className={`truncate text-xs text-gray-500 ${dk('dark:text-gray-400')}`}>
                                {user?.is_internal ? 'Internal User' : 'User'}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="space-y-0.5">
                        {[...sectionItems, ...accountItems].map((item) => (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => runItem(item, setIsMobileMenuOpen)}
                            className={menuRow(item)}
                          >
                            <item.icon className={menuIcon(item)} strokeWidth={1.75} />
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default HomeNavMenu;
